/**
 * Atomic Reset Service for Cache Invalidation System
 * 
 * Provides atomic reset functionality with cross-tab coordination using BroadcastChannel.
 * Ensures all browser tabs and components reset their caches simultaneously when 
 * database changes are detected.
 * 
 * Phase 4 implementation - enhanced with selective invalidation and namespaced storage
 */

import { clearStoredMetaVersion } from './metaVersion';
import { clearStoredBootTime } from './developmentResetDetection';
import { 
  clearNamespaceStorage, 
  migrateLegacyKeys, 
  needsMigration,
  type StorageNamespace 
} from './namespacedStorageService';
import { detectCommunityContext } from './communityAwareCacheInvalidation';

// Global flag to indicate reset is in progress
declare global {
  interface Window {
    __TERRARIUM_RESETTING__?: boolean;
  }
}

// BroadcastChannel for cross-tab communication
const RESET_CHANNEL_NAME = 'terrarium-cache-reset';
let resetChannel: BroadcastChannel | null = null;

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// Reset event types
export interface ResetEvent {
  type: 'RESET_START' | 'RESET_COMPLETE' | 'RESET_ERROR';
  tabId: string;
  metaVersion: string;
  timestamp: number;
  reason: string;
}

// Generate unique tab identifier
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Initialize the BroadcastChannel for cross-tab communication
 * Must be called early in app lifecycle
 */
export function initializeResetChannel(): void {
  if (typeof window === 'undefined' || resetChannel) {
    return; // Skip in SSR or if already initialized
  }

  try {
    resetChannel = new BroadcastChannel(RESET_CHANNEL_NAME);
    
    resetChannel.addEventListener('message', (event: MessageEvent<ResetEvent>) => {
      const { type, tabId, metaVersion, reason } = event.data;
      
      // Don't process messages from the same tab
      if (tabId === TAB_ID) {
        return;
      }
      
      if (isDevelopment) {
        console.log(`AtomicReset: Received ${type} from tab ${tabId}`, { metaVersion, reason });
      }
      
      // Handle reset coordination from other tabs
      if (type === 'RESET_START') {
        handleCrossTabResetStart(metaVersion, reason).catch(error => {
          console.error('AtomicReset: Cross-tab reset failed:', error);
        });
      } else if (type === 'RESET_COMPLETE') {
        handleCrossTabResetComplete();
      }
    });
    
    if (isDevelopment) {
      console.log('AtomicReset: BroadcastChannel initialized for tab', TAB_ID);
    }
  } catch (error) {
    console.error('AtomicReset: Failed to initialize BroadcastChannel:', error);
  }
}

/**
 * Cleanup BroadcastChannel (called on app unmount)
 */
export function cleanupResetChannel(): void {
  if (resetChannel) {
    resetChannel.close();
    resetChannel = null;
  }
}

/**
 * Set the global reset flag to indicate reset is in progress
 */
export function setResetFlag(isResetting: boolean): void {
  window.__TERRARIUM_RESETTING__ = isResetting;
  
  if (isDevelopment) {
    console.log('AtomicReset: Global reset flag set to', isResetting);
  }
}

/**
 * Check if a reset is currently in progress
 */
export function isResetInProgress(): boolean {
  return window.__TERRARIUM_RESETTING__ === true;
}

/**
 * Broadcast reset event to all tabs
 */
function broadcastResetEvent(event: ResetEvent): void {
  if (!resetChannel) {
    console.warn('AtomicReset: BroadcastChannel not initialized');
    return;
  }
  
  try {
    resetChannel.postMessage(event);
  } catch (error) {
    console.error('AtomicReset: Failed to broadcast reset event:', error);
  }
}

/**
 * Handle cross-tab reset start - coordinate with other tabs
 */
async function handleCrossTabResetStart(metaVersion: string, reason: string): Promise<void> {
  if (isResetInProgress()) {
    return; // Already resetting
  }
  
  console.log('AtomicReset: Coordinating reset due to cross-tab signal');
  setResetFlag(true);
  
  try {
    // Perform local reset without broadcasting (to avoid loops)
    await performLocalReset(metaVersion, reason, false);
  } finally {
    setResetFlag(false);
  }
}

/**
 * Handle cross-tab reset completion
 */
function handleCrossTabResetComplete(): void {
  if (isDevelopment) {
    console.log('AtomicReset: Cross-tab reset completed');
  }
  setResetFlag(false);
}

/**
 * Perform local cache reset operations (Phase 4 enhanced with namespaced storage)
 */
async function performLocalReset(metaVersion: string, reason: string, shouldBroadcast: boolean = true): Promise<void> {
  let keysCleared = 0;
  const errors: string[] = [];

  try {
    // Phase 4: Perform migration if needed before clearing
    if (needsMigration()) {
      if (isDevelopment) {
        console.log('AtomicReset: Performing legacy key migration before reset');
      }
      
      try {
        const migrationResult = await migrateLegacyKeys();
        if (migrationResult.success && migrationResult.data) {
          if (isDevelopment) {
            console.log('AtomicReset: Migration completed', {
              migrated: migrationResult.data.migrated.length,
              errors: migrationResult.data.errors.length
            });
          }
          errors.push(...migrationResult.data.errors);
        }
      } catch (error) {
        errors.push(`Migration failed: ${error}`);
      }
    }

    // Clear meta-version tracking (legacy)
    clearStoredMetaVersion();
    
    // Clear development boot time tracking (legacy)
    clearStoredBootTime();
    
    // Phase 4: Use namespaced storage clearing for development environment
    const isDev = process.env.NODE_ENV === 'development';
    const communityContext = detectCommunityContext();
    
    if (isDev) {
      // Development - more aggressive clearing but preserve user preferences
      const namespaceOptions: Partial<StorageNamespace> = {
        env: 'dev',
        // Don't specify community to clear all communities in dev env
      };
      
      const clearResult = clearNamespaceStorage(namespaceOptions);
      if (clearResult.success && clearResult.data) {
        keysCleared += clearResult.data.length;
        
        if (isDevelopment) {
          console.log('AtomicReset: Namespaced storage cleared', {
            keysCleared: clearResult.data.length,
            keys: clearResult.data.slice(0, 5) // Show first 5 keys
          });
        }
      } else {
        errors.push(`Namespaced storage clearing failed: ${clearResult.error}`);
      }
    } else {
      // Production - more conservative clearing, only current community
      const namespaceOptions: Partial<StorageNamespace> = {
        env: 'prod',
        community: communityContext.hostname,
        global: false, // Don't clear global user preferences in production
      };
      
      const clearResult = clearNamespaceStorage(namespaceOptions);
      if (clearResult.success && clearResult.data) {
        keysCleared += clearResult.data.length;
      } else {
        errors.push(`Community storage clearing failed: ${clearResult.error}`);
      }
    }
    
    // Phase 4: Fallback to legacy clearing for any remaining keys
    const legacyKeysToRemove = [
      'terrarium_meta_version',
      'terrarium_db_boot_time',
      // Legacy keys that might not have been migrated yet
      'terrarium_community_branding_v4',
      'terrarium_critical_branding_v4',
      'terrarium_community_member_onboarding'
    ];
    
    legacyKeysToRemove.forEach(key => {
      try {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
          keysCleared++;
        }
      } catch (error) {
        errors.push(`Failed to remove legacy key ${key}: ${error}`);
      }
    });
    
    if (isDevelopment) {
      console.log('AtomicReset: Local reset completed', { 
        metaVersion, 
        reason, 
        keysCleared,
        errors: errors.length,
        phase: 'Phase 4 Enhanced'
      });
    }
    
    // Broadcast completion to other tabs
    if (shouldBroadcast) {
      broadcastResetEvent({
        type: 'RESET_COMPLETE',
        tabId: TAB_ID,
        metaVersion,
        timestamp: Date.now(),
        reason
      });
    }
    
  } catch (error) {
    const errorMsg = `AtomicReset: Local reset failed: ${error}`;
    console.error(errorMsg);
    errors.push(errorMsg);
    
    if (shouldBroadcast) {
      broadcastResetEvent({
        type: 'RESET_ERROR',
        tabId: TAB_ID,
        metaVersion,
        timestamp: Date.now(),
        reason: `Reset failed: ${error}`
      });
    }
  }
}

/**
 * Initiate atomic reset across all tabs
 * This is the main entry point called when database changes are detected
 */
export async function initiateAtomicReset(metaVersion: string, reason: string): Promise<void> {
  if (isResetInProgress()) {
    console.log('AtomicReset: Reset already in progress, skipping');
    return;
  }
  
  console.log('AtomicReset: Initiating atomic reset', { metaVersion, reason });
  
  // Set global flag first
  setResetFlag(true);
  
  try {
    // Broadcast reset start to other tabs
    broadcastResetEvent({
      type: 'RESET_START',
      tabId: TAB_ID,
      metaVersion,
      timestamp: Date.now(),
      reason
    });
    
    // Perform local reset
    await performLocalReset(metaVersion, reason);
    
    // Add a small delay to ensure cross-tab coordination
    await new Promise(resolve => setTimeout(resolve, 100));
    
  } catch (error) {
    console.error('AtomicReset: Atomic reset failed:', error);
  } finally {
    // Always clear the flag after attempt
    setResetFlag(false);
  }
}

/**
 * Development utility to manually trigger a reset
 */
export function manualReset(reason: string = 'Manual trigger'): Promise<void> {
  if (!isDevelopment) {
    console.warn('AtomicReset: Manual reset only available in development');
    return Promise.resolve();
  }
  
  const metaVersion = Date.now().toString();
  return initiateAtomicReset(metaVersion, reason);
}